/* ==========================================================================
   Royal Care Professional — shoppable video carousel

   Two jobs, both progressive enhancement. Without this file the section is a
   working rail of images that link to products; with it the third-party videos
   become playable and the uploaded ones play on hover.

     1. Embed facade. YouTube / Vimeo / Instagram cards render as a cover image
        plus a play button. The iframe is built on the first click, which keeps
        four third-party players off the page on load — and is the only way a
        card with an iframe can also be a link, since an iframe swallows every
        pointer event that reaches it.

     2. Hover playback for uploaded video. play() returns a promise that
        rejects if the pointer leaves before the first frame decodes, so the
        rejection is swallowed rather than logged, and an IntersectionObserver
        pauses anything scrolled out of the rail so a long row is not decoding
        video nobody can see.

   Idempotent — init() claims nodes once, so the theme editor can re-run it on
   every section load.
   ========================================================================== */

(function () {
  'use strict';

  if (window.RCVideoCarousel) {
    window.RCVideoCarousel.init();
    return;
  }

  var NO_HOVER = '(hover: none)';

  /* ---- the play button ------------------------------------------------ */

  /**
   * Two kinds of card share one button.
   *
   * With data-embed it builds the iframe, and the media stops being part of the
   * card link — a third-party player owns its own clicks.
   *
   * Without, the card holds an uploaded video set to play on click. That one
   * never gets native controls: the button itself becomes pause, which is what
   * lets the whole card stay a single link to the product.
   */
  function play(button) {
    var media = button.closest('[data-rc-vc-media]');
    var card = button.closest('.rc-vc__card');
    var src = button.getAttribute('data-embed');

    if (!media) return;

    if (!src) {
      var local = media.querySelector('.rc-vc__video');
      if (!local) return;

      if (local.paused) {
        // Clicking is a user gesture, so sound is allowed from here.
        local.muted = false;
        start(local);
        if (card) card.classList.add('is-video-on');
        button.setAttribute('aria-label', 'Pause video');
      } else {
        local.pause();
        if (card) card.classList.remove('is-video-on');
        button.setAttribute('aria-label', 'Play video');
      }
      return;
    }

    var frame = document.createElement('iframe');
    frame.className = 'rc-vc__frame';
    frame.src = src;
    frame.title = button.getAttribute('data-title') || 'Video';
    frame.loading = 'lazy';
    frame.allow =
      'accelerometer; autoplay; clipboard-write; encrypted-media; gyroscope; picture-in-picture; web-share';
    frame.setAttribute('allowfullscreen', '');
    frame.setAttribute('referrerpolicy', 'strict-origin-when-cross-origin');

    media.appendChild(frame);
    if (card) card.classList.add('is-playing');
  }

  /* ---- uploaded video ------------------------------------------------- */

  /**
   * Point the <source> elements at the real files and pull the first frame.
   * Until this runs the card has cost nothing but its cover image, which is how
   * a rail of five videos stays off the page-load budget.
   *
   * is-live rather than is-loaded: the class tracks whether the video is
   * actually running, so a paused card falls back to the cover the merchant
   * chose instead of freezing on an arbitrary frame.
   */
  function hydrate(video) {
    var sources = video.querySelectorAll('source[data-src]');
    if (!sources.length) return;

    Array.prototype.forEach.call(sources, function (source) {
      source.src = source.getAttribute('data-src');
      source.removeAttribute('data-src');
    });

    video.addEventListener('playing', function () {
      video.classList.add('is-live');
    });

    video.addEventListener('pause', function () {
      video.classList.remove('is-live');
    });

    // preload="none" in the markup is what kept the file off the page load;
    // now that it is wanted, ask for enough to paint a frame.
    video.preload = 'metadata';
    video.load();
  }

  var loader = null;

  function lazy(video) {
    if (!window.IntersectionObserver) {
      hydrate(video);
      return;
    }

    if (!loader) {
      loader = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (!entry.isIntersecting) return;
            loader.unobserve(entry.target);
            hydrate(entry.target);
          });
        },
        // A margin, not a bare threshold: the file should be ready by the time
        // the card is actually on screen, not start downloading then.
        { rootMargin: '400px 0px' }
      );
    }

    loader.observe(video);
  }

  /** play() rejects on an interrupted start; that is expected, not an error. */
  function start(video) {
    // A hover that beats the observer would otherwise play a source-less video.
    hydrate(video);

    var attempt = video.play();
    if (attempt && typeof attempt.catch === 'function') attempt.catch(function () {});
  }

  function stop(video) {
    video.pause();
    video.currentTime = 0;
  }

  function bindHover(card) {
    var video = card.querySelector('.rc-vc__video');
    if (!video) return;

    card.addEventListener('mouseenter', function () {
      start(video);
    });

    card.addEventListener('mouseleave', function () {
      stop(video);
    });

    // Keyboard users never fire mouseenter, so the card's own link doubles as
    // the trigger when it takes focus.
    card.addEventListener('focusin', function () {
      start(video);
    });

    card.addEventListener('focusout', function () {
      if (!card.contains(document.activeElement)) stop(video);
    });
  }

  /**
   * Autoplaying cards only run while they are on screen. Built lazily so a page
   * with no autoplay cards never creates the observer.
   */
  var watcher = null;

  function watch(video) {
    if (!window.IntersectionObserver) return;

    if (!watcher) {
      watcher = new IntersectionObserver(
        function (entries) {
          entries.forEach(function (entry) {
            if (entry.isIntersecting) start(entry.target);
            else entry.target.pause();
          });
        },
        { threshold: 0.25 }
      );
    }

    watcher.observe(video);
  }

  /* ---- boot ------------------------------------------------------------ */

  function init(scope) {
    var root = scope || document;

    Array.prototype.forEach.call(root.querySelectorAll('[data-rc-vc-play]'), function (button) {
      if (button.rcVcBound) return;
      button.rcVcBound = true;

      button.addEventListener('click', function (event) {
        event.preventDefault();
        play(button);
      });
    });

    // A touch screen never fires mouseenter, so "play on hover" would leave the
    // card frozen on its poster. There it falls back to playing in view, which
    // is the behaviour the setting is actually reaching for.
    var touch = window.matchMedia && window.matchMedia(NO_HOVER).matches;

    Array.prototype.forEach.call(root.querySelectorAll('[data-rc-vc-hover]'), function (card) {
      if (card.rcVcBound) return;
      card.rcVcBound = true;

      if (!touch) {
        bindHover(card);
        return;
      }

      var video = card.querySelector('.rc-vc__video');
      if (video) {
        video.rcVcBound = true;
        watch(video);
      }
    });

    Array.prototype.forEach.call(root.querySelectorAll('.rc-vc__video[autoplay]'), function (video) {
      if (video.rcVcBound) return;
      video.rcVcBound = true;
      watch(video);
    });

    // Deferred loading is separate from playback, and every uploaded video
    // wants it — hover, autoplay and click alike.
    Array.prototype.forEach.call(root.querySelectorAll('[data-rc-vc-lazy]'), function (video) {
      if (video.rcVcLazy) return;
      video.rcVcLazy = true;
      lazy(video);
    });
  }

  window.RCVideoCarousel = { init: init };

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', function () {
      init();
    });
  } else {
    init();
  }

  document.addEventListener('shopify:section:load', function (event) {
    init(event.target);
  });
})();
